import { useState } from 'react';
import {
  REACT_APP_OPENZK_RPC,
  REACT_APP_OZETH_L2_TOKEN,
  REACT_APP_OZUSD_L2_TOKEN,
} from '../Config/Config';
import Web3, { ContractAbi } from 'web3';
import { BrowserProvider, L1Signer, Provider } from 'zksync-ethers';
import { Connection } from '@wagmi/core';
import BigNumber from 'bignumber.js';

export enum Status {
  'claimed' = 1,
  'pending' = 2,
  'claim' = 3,
}

type Transaction = {
  txHash: string;
  description: string;
  status: Status;
};

/** Only scan recent history to avoid excessive eth_getLogs RPC usage. */
const LOG_LOOKBACK_BLOCKS = 1500;

function isValidLogContractAddress(web3: Web3, addr: string | undefined): boolean {
  if (addr === undefined || addr === null) return false;
  const s = String(addr).trim();
  if (!s) return false;
  return web3.utils.isAddress(s);
}

function rawAmountToDisplay(raw: unknown, decimals: number): string {
  const s =
    typeof raw === 'bigint'
      ? raw.toString()
      : typeof raw === 'string'
        ? raw
        : String(raw ?? '0');
  return BigNumber(s).div(BigNumber(10).pow(decimals)).toFixed(4, 1);
}

/**
 * Fetches event logs via L2 JSON-RPC (eth_getLogs) for the latest
 * {@link LOG_LOOKBACK_BLOCKS} blocks only. OpenZK explorer REST v2 does not
 * expose Etherscan-compatible module=logs/getLogs; see
 * https://explorer.openzk.net/api-docs
 */
async function fetchLogsViaRpc(
  web3: Web3,
  params: {
    contractAddress: string;
    topic0: string;
    topic1?: string;
    /** When omitted, one eth_blockNumber call is made. */
    latestBlock?: number;
  },
): Promise<any[]> {
  if (!isValidLogContractAddress(web3, params.contractAddress)) {
    console.warn(
      '[useL2Transactions] Skipping getPastLogs: missing or invalid contract address (check REACT_APP_OZETH_L2_TOKEN / REACT_APP_OZUSD_L2_TOKEN / token list).',
    );
    return [];
  }

  const latestBlock = Number(
    params.latestBlock ?? (await web3.eth.getBlockNumber()),
  );
  const fromBlock = Math.max(1, latestBlock - LOG_LOOKBACK_BLOCKS + 1);

  const topics: (string | null)[] = [params.topic0];
  if (params.topic1) {
    topics.push(params.topic1);
  }

  try {
    return await web3.eth.getPastLogs({
      fromBlock: fromBlock,
      toBlock: latestBlock,
      address: params.contractAddress.trim(),
      topics,
    });
  } catch (error) {
    console.error(
      `Error fetching logs via RPC for blocks ${fromBlock}-${latestBlock}:`,
      error,
    );
    return [];
  }
}

export function useL2Transactions(address: string) {
  const web3 = new Web3(REACT_APP_OPENZK_RPC);

  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(false);

  const loadL2Transactions = async (
    connections: Connection,
    tokens: {
      contract: string;
      symbol: string;
      abi: ContractAbi;
      decimals: number;
    }[],
  ) => {
    console.log({ tokens });
    if (!address || !connections || !tokens) {
      return;
    }

    if (!connections[0]?.connector?.getProvider) {
      return;
    }

    const walletProvider = await connections[0].connector.getProvider();

    const provider = new BrowserProvider(walletProvider);

    const zkProvider = new Provider(REACT_APP_OPENZK_RPC);

    const signer = L1Signer.from(await provider.getSigner(), zkProvider);

    setLoading(true);

    const paddedAddress = '0x' + address.slice(2).padStart(64, '0');
    const latestBlock = Number(await web3.eth.getBlockNumber());

    const resultEth = await fetchLogsViaRpc(web3, {
      contractAddress: REACT_APP_OZETH_L2_TOKEN as string,
      topic0:
        '0x2717ead6b9200dd235aad468c9809ea400fe33ac69b5bfaa6d3e90fc922b6398',
      topic1: paddedAddress.toLowerCase(),
      latestBlock,
    });

    const resultUsd = await fetchLogsViaRpc(web3, {
      contractAddress: REACT_APP_OZUSD_L2_TOKEN as string,
      topic0:
        '0x9b5b9a05e4726d8bb959f1440e05c6b8109443f2083bc4e386237d7654526553',
      topic1: paddedAddress.toLowerCase(),
      latestBlock,
    });

    const reversedList = resultEth?.map((e, i, a) => a[a.length - 1 - i]);

    const reversedUsdList = resultUsd?.map((e, i, a) => a[a.length - 1 - i]);

    const transactionHistory = [];

    // Process ozETH transactions (sequential RPCs to avoid rate limits).
    if (reversedList && reversedList.length > 0) {
      for (const event of reversedList) {
        if (!event || !event.data || !event.topics || event.topics.length < 3) {
          continue;
        }

        let bridgeData: { amount?: unknown };
        try {
          bridgeData = web3.eth.abi.decodeLog(
            [
              { indexed: true, type: 'address', name: 'from' },
              { indexed: true, type: 'address', name: 'recipient' },
              { indexed: false, type: 'uint256', name: 'amount' },
            ],
            event.data,
            [event.topics[1], event.topics[2]],
          ) as { amount?: unknown };
        } catch {
          console.warn(
            '[useL2Transactions] Skipping log: ozETH bridge decode failed (wrong contract, RPC, or event layout).',
            event.transactionHash,
          );
          continue;
        }

        try {
          const isWithdrawalFinalized = await signer.isWithdrawalFinalized(
            event.transactionHash,
          );
          if (isWithdrawalFinalized) {
            continue;
          }

          const details = await zkProvider.getTransactionDetails(
            event.transactionHash,
          );
          if (!!details.ethExecuteTxHash) {
            transactionHistory.push({
              txHash: event.transactionHash,
              description: `Bridge ${rawAmountToDisplay(bridgeData.amount, 18)} ozETH`,
              status: Status.claim,
            });
          } else {
            transactionHistory.push({
              txHash: event.txHash || event.transactionHash,
              description: `Bridge ${rawAmountToDisplay(bridgeData.amount, 18)} ozETH`,
              status: Status.pending,
            });
          }
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : String(error);
          console.log(errorMessage);
          if (errorMessage === 'Log proof not found!') {
            transactionHistory.push({
              txHash: event.txHash || event.transactionHash,
              description: `Bridge ${rawAmountToDisplay(bridgeData.amount, 18)} ozETH`,
              status: Status.pending,
            });
          }
        }
      }
    }

    // loop through tokens with for each
    for (const token of tokens) {
      if (token.symbol === 'ozETH') {
        // Skip ozETH as it's already processed above using the filtered results
        continue;
      } else {
        // Fetch custom token transactions with pagination
        const customTokenResults = await fetchLogsViaRpc(web3, {
          contractAddress: token.contract,
          topic0:
            '0x9b5b9a05e4726d8bb959f1440e05c6b8109443f2083bc4e386237d7654526553',
          topic1: paddedAddress.toLowerCase(),
          latestBlock,
        });
        
        const reversedUsdList = customTokenResults?.map(
          (e, i, a) => a[a.length - 1 - i],
        );

        if (reversedUsdList && reversedUsdList.length > 0) {
          for (const event of reversedUsdList) {
            if (!event?.data || !event.topics || event.topics.length < 2) {
              continue;
            }

            let bridgeData: { _account?: string; _amount?: unknown };
            try {
              bridgeData = web3.eth.abi.decodeLog(
                [
                  { indexed: true, type: 'address', name: '_account' },
                  { indexed: false, type: 'uint256', name: '_amount' },
                ],
                event.data,
                [event.topics[1]],
              ) as { _account?: string; _amount?: unknown };
            } catch {
              console.warn(
                '[useL2Transactions] Skipping log: token event decode failed (ABI/layout mismatch).',
                event.transactionHash,
              );
              continue;
            }

            try {
              if (
                address.toLowerCase() !==
                String(bridgeData._account ?? '').toLowerCase()
              ) {
                continue;
              }

              const isWithdrawalFinalized =
                await signer.isWithdrawalFinalized(event.transactionHash);
              if (isWithdrawalFinalized) {
                continue;
              }

              const details = await zkProvider.getTransactionDetails(
                event.transactionHash,
              );
              if (!!details.ethExecuteTxHash) {
                transactionHistory.push({
                  txHash: event.transactionHash,
                  description: `Bridge ${rawAmountToDisplay(bridgeData._amount, token.decimals)} ${token.symbol}`,
                  status: Status.claim,
                });
              } else {
                transactionHistory.push({
                  txHash: event.txHash,
                  description: `Bridge ${rawAmountToDisplay(bridgeData._amount, token.decimals)} ${token.symbol}`,
                  status: Status.pending,
                });
              }
            } catch (error) {
              const errorMessage =
                error instanceof Error ? error.message : String(error);
              console.log(errorMessage);
              if (errorMessage === 'Log proof not found!') {
                transactionHistory.push({
                  txHash: event.txHash,
                  description: `Bridge ${rawAmountToDisplay(bridgeData._amount, token.decimals)} ${token.symbol}`,
                  status: Status.pending,
                });
              }
            }
          }
        }
      }
    }
    setLoading(false);

    setTransactions(transactionHistory);
  };

  const resetTransactions = async () => {
    setTransactions([]);
  };

  return { loading, transactions, loadL2Transactions, resetTransactions };
}
